import { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
import {
    Observable,
    combineLatest,
    concatMap,
    from,
    of,
    reduce,
    switchMap,
} from "rxjs";

import {
    FileValidatorInterface,
    ValidationReturnValue,
} from "./FileValidatorInterface";

class ImageFileValidator implements FileValidatorInterface {
    constructor(private readonly gwI18n: GwI18n) {}

    // Основной метод для валидации файлов
    public validateFiles(files: File[]): Observable<ValidationReturnValue[]> {
        return from(files).pipe(
            concatMap((file) => this.validateFile(file)), // Последовательная обработка файлов
            reduce(
                (
                    acc: ValidationReturnValue[],
                    result: ValidationReturnValue
                ) => [...acc, result],
                [] as ValidationReturnValue[] // Инициализация массива результатов
            )
        );
    }

    // Метод для валидации отдельного файла
    public validateFile(file: File): Observable<ValidationReturnValue> {
        // Используем combineLatest для одновременной загрузки переводов
        return combineLatest([
            this.gwI18n.selectT("common.MediaUploader", "invalid_size", {
                maxSize: "5 MB",
                minSize: "5 KB",
            }),
            this.gwI18n.selectT("common.MediaUploader", "invalid_format"),
            this.gwI18n.selectT("common.MediaUploader", "invalid_image"),
            this.gwI18n.selectT("common.MediaUploader", "read_error"),
        ]).pipe(
            switchMap(
                ([
                    invalidSizeError,
                    invalidFormatError,
                    invalidImageError,
                    readError,
                ]) => {
                    const { size, type } = file;

                    // 1. Проверка размера файла
                    if (!this.isValidSize(size)) {
                        return of({
                            valid: false,
                            file,
                            error: invalidSizeError,
                        });
                    }

                    // 2. Проверка MIME-типа (формата файла)
                    if (!this.isImage(type)) {
                        return of({
                            valid: false,
                            file,
                            error: invalidFormatError,
                        });
                    }

                    // 3. Проверка содержимого изображения
                    return this.validateImageContent(
                        file,
                        invalidImageError,
                        readError
                    );
                }
            )
        );
    }

    // Метод для проверки содержимого изображения
    private validateImageContent(
        file: File,
        invalidImageError: string,
        readError: string
    ): Observable<ValidationReturnValue> {
        const fileReader = new FileReader();

        return new Observable<ValidationReturnValue>((observer) => {
            fileReader.onload = () => {
                const image = new Image();
                image.onload = () => {
                    observer.next({ valid: true, file, error: null });
                    observer.complete();
                };
                image.onerror = () => {
                    observer.next({
                        valid: false,
                        file,
                        error: invalidImageError,
                    });
                    observer.complete();
                };
                image.src = fileReader.result as string;
            };

            fileReader.onerror = () => {
                observer.next({
                    valid: false,
                    file,
                    error: readError,
                });
                observer.complete();
            };

            fileReader.readAsDataURL(file);
        });
    }

    // Проверка размера файла (в килобайтах)
    private isValidSize(size: number): boolean {
        const sizeKB = size / 1024; // Размер файла в килобайтах
        return sizeKB >= 5 && sizeKB <= 5120; // От 5 КБ до 5 МБ
    }

    // Проверка, является ли файл изображением
    private isImage(mimeType: string): boolean {
        return mimeType.startsWith("image/"); // Проверка MIME-типа
    }
}

export default ImageFileValidator;
